import { ProductionMaterialEvidence } from "../ProductionMaterialEvidence";
import { AlertTriangle, Check, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, FilePlus2, FileSpreadsheet, LoaderCircle, Plus, RefreshCw, X } from "lucide-react";
import { createPortal } from "react-dom";
import { PlanScheduleReview } from "../PlanScheduleReview";
import { ProductionWorkOrderTree } from "../ProductionWorkOrderTree";
import { PlanWorkbookEditor } from "../PlanWorkbookEditor";

import { procurementActionLabel, canRequestMaterial, procurementView } from "../production-material-procurement";
import { formatDate, formatQuantity, planRowStatusLabel, productStageLabel } from "./plan-import-presentation";
import { requestQuantity } from "./plan-request-rules";

import type { ProductionPlanImportWorkspace } from "./useProductionPlanImportWorkspace";
import { workOrderConnection } from "./WorkOrderConnection";
export function ProductionPlanImportView({workspace}: {workspace: ProductionPlanImportWorkspace}) {
  const {
    selectFile,
    uploadOpen,
    savedPlans,
    setWeekMenuOpen,
    weekMenuOpen,
    plansLoading,
    selectedSavedPlan,
    selectedPlanId,
    loadSavedPlan,
    loadingStep,
    refreshSelectedPlan,
    preview,
    startNewWeek,
    cancelNewWeek,
    file,
    inspection,
    error,
    nativePlanSelected,
    workbookWorkspace,
    mapping,
    calculate,
    inspectFile,
    activeTab,
    selectTab,
    currentAssessment,
    activeRequestError,
    activeRequestsReady,
    groupedReviews,
    validRefreshTime,
    requestReviewOpen,
    setRequestReviewOpen,
    requestReviewStep,
    requestDrafts,
    overdueDraftMaterials,
    overdueOverrideAcknowledged,
    setOverdueOverrideAcknowledged,
    overdueOverrideReason,
    setOverdueOverrideReason,
    setRequestDrafts,
    today,
    rawMaterialRequestCount,
    packagingRequestCount,
    changedRequestCount,
    criticalRequestCount,
    creatingRequests,
    requestDraftInvalid,
    saveRequestBatch,
    reviewSelectedRequests,
    setRequestReviewStep,
    selectedMaterialCodes,
    setSelectedMaterialCodes,
    openRequestReview,
    visibleMaterials,
    allVisibleSelected,
    visibleSelectableMaterials,
    activeRequests,
    toggleMaterial,
    openSingleRequestReview,
    visiblePlanRows,
    shouldPaginate,
    activeItemCount,
    pageStart,
    pageEnd,
    page,
    setPage,
    totalPages,
  } = workspace;
return <section className="module-card production-plan-import" aria-labelledby="production-plan-import-title">
    <header>
      <div>
        <h2 id="production-plan-import-title">Haftalık üretim planı</h2>
        {!uploadOpen && savedPlans.length > 0 ? <div
          className="production-plan-week-selector"
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setWeekMenuOpen(false);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setWeekMenuOpen(false);
          }}
        >
          <div className="production-plan-week-dropdown">
            <button
              type="button"
              aria-label="Plan haftası"
              aria-haspopup="listbox"
              aria-expanded={weekMenuOpen}
              disabled={plansLoading}
              onClick={() => setWeekMenuOpen((current) => !current)}
            >
              <span>{(() => {
                const selected = selectedSavedPlan!;
                return `${formatDate(selected.weekStart)} – ${formatDate(selected.weekEnd)} · v${selected.version}${selected.status === "SUPERSEDED" ? " · Arşiv" : ""}`;
              })()}</span>
              <ChevronDown size={16} aria-hidden="true" />
            </button>
            {weekMenuOpen ? <div className="production-plan-week-options" role="listbox" aria-label="Plan haftası">
              {savedPlans.map((plan) => {
                const selected = plan.id === selectedPlanId;
                return <button
                  key={plan.id}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={selected ? "is-selected" : ""}
                  onClick={() => {
                    setWeekMenuOpen(false);
                    if (!selected) void loadSavedPlan(plan.id);
                  }}
                >
                  <span>{formatDate(plan.weekStart)} – {formatDate(plan.weekEnd)} · v{plan.version}{plan.status === "SUPERSEDED" ? " · Arşiv" : " · Güncel"}</span>
                  {selected ? <Check size={15} aria-hidden="true" /> : null}
                </button>;
              })}
            </div> : null}
          </div>
        </div> : null}
      </div>
      <div className="production-plan-header-actions">
        {!uploadOpen && selectedPlanId && selectedSavedPlan?.status === "ACTIVE" ? <button type="button" disabled={loadingStep !== null || plansLoading} onClick={() => void refreshSelectedPlan()}>
          <RefreshCw className={loadingStep === "CALCULATE" ? "is-spinning" : ""} size={15} /> {preview?.materialAssessment === "NOT_REQUESTED" ? "Reçete ve stokla karşılaştır" : "Mikro’dan yenile"}
        </button> : null}
        {!uploadOpen ? <button type="button" disabled={plansLoading || loadingStep !== null} onClick={startNewWeek}><Plus size={16} /> Haftalık Excel yükle</button> : savedPlans.length > 0 ? <button type="button" disabled={loadingStep !== null} onClick={cancelNewWeek}>Kayıtlı plana dön</button> : null}
      </div>
    </header>

    {uploadOpen ? <div className="production-plan-upload">
      <p className="weekly-plan-upload-help" id="weekly-plan-upload-help">O haftanın Excel dosyasını seçin. Dosya düzenlenebilir taslağa alınır; satırlar planlama kontrolü ve onayı olmadan operatör ekranına aktarılmaz.</p>
      <label className={`production-plan-file ${file ? "is-selected" : ""}`}>
        {loadingStep !== null
          ? <LoaderCircle className="is-spinning" size={19} aria-hidden="true" />
          : <FileSpreadsheet size={19} aria-hidden="true" />}
        <span>
          <strong>{file ? file.name : "Excel üretim planını seçin"}</strong>
          <small>{!file
            ? ".xlsx · En fazla 10 MB"
            : loadingStep !== null
              ? loadingStep === "INSPECT" ? "Excel okunuyor ve plan doğrulanıyor…" : "Excel düzenlenebilir taslağa aktarılıyor…"
              : inspection
                ? "Dosya kontrolü tamamlandı"
                : "Dosya seçildi"}</small>
        </span>
        <span className="production-plan-file-action">
          {file ? "Değiştir" : "Dosya seç"}
        </span>
        <input
          type="file"
          disabled={loadingStep !== null || plansLoading}
          aria-describedby="weekly-plan-upload-help"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={selectFile}
        />
      </label>
    </div> : plansLoading ? <div className="production-plan-loading"><LoaderCircle className="is-spinning" size={18} /> Kayıtlı plan açılıyor…</div> : null}
    {error ? <div className="production-plan-message is-error" role="alert"><AlertTriangle size={17} />{error}</div> : null}

    {loadingStep ? <p className="weekly-plan-progress" role="status" aria-live="polite">{loadingStep === "INSPECT" ? "Plan kontrol ediliyor…" : "Plan aktarılıyor. Bu işlem tamamlanana kadar bekleyin."}</p> : null}
    {nativePlanSelected ? <p className="production-material-assessment-note">Bu plan sistemde oluşturuldu. Düzenlemek için Haftalık plan sekmesine geçin; kaynak Excel dosyası gerekmiyor.</p> : null}
    <PlanWorkbookEditor workspace={workbookWorkspace} readOnly={!uploadOpen && selectedSavedPlan?.status === "SUPERSEDED"} canSave={Boolean(file && inspection?.weekStart && mapping && inspection.missingFields.length === 0)} saving={loadingStep !== null || plansLoading} onSave={() => void calculate()} />
    {inspection && !preview && loadingStep === null ? <section className="production-plan-inspection" aria-labelledby="production-plan-inspection-title">
      <header>
        <div>
          <h3 id="production-plan-inspection-title">Plan taslağı</h3>
          <small>{inspection.sheetName}</small>
        </div>
        <span className={inspection.missingFields.length > 0 || inspection.planAudit?.issues.some(i=>i.severity==="error") ? "is-warning" : "is-ready"}>
          {inspection.missingFields.length > 0 || inspection.planAudit?.issues.some(i=>i.severity==="error") ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
          {inspection.missingFields.length > 0 ? "Dosya düzeni tanınamadı" : inspection.planAudit?.issues.some(i=>i.severity==="error") ? "Eksikler taslakta düzenlenecek" : "Taslağa aktarılmaya hazır"}
        </span>
      </header>

      <div className="production-plan-inspection-summary">
        <article><small>Plan dönemi</small><strong>{inspection.summary.firstPlanDate ? `${formatDate(inspection.summary.firstPlanDate)}${inspection.summary.lastPlanDate && inspection.summary.lastPlanDate !== inspection.summary.firstPlanDate ? ` – ${formatDate(inspection.summary.lastPlanDate)}` : ""}` : "Tarih bulunamadı"}</strong></article>
        <article><small>Mamul planı</small><strong>{inspection.summary.eligibleProductCount}</strong></article>
        <article><small>Günlük dağılım</small><strong>{inspection.mapping.dailyQuantityColumns.length > 0 ? `${inspection.mapping.dailyQuantityColumns.length} gün` : "Bulunamadı"}</strong></article>
        <article><small>Hesap kapsamı</small><strong>Hammadde + ambalaj</strong></article>
      </div>

      {inspection.missingFields.length > 0 ? <p role="alert">Ürün kodu, ürün adı veya miktar başlığı tanınamadı. Mevcut plan korunuyor. Yüklediğiniz dosyayı sistem sorumlusuna iletin; sütun eşleştirmeniz gerekmiyor.</p> : null}

      {inspection.calendar ? <PlanScheduleReview data={inspection} weekStart={inspection.weekStart} busy={loadingStep !== null} onCalendarChange={value=>void inspectFile(mapping, undefined, value)}/> : null}
      {inspection.missingFields.length === 0 ? <div className="production-plan-confirmation">
        {!workbookWorkspace.workbook && inspection.weekStart ? <button className="primary-button" type="button" onClick={() => void calculate()}><RefreshCw size={17} /> Düzenlenebilir taslağa aktar</button> : null}
      </div> : null}
    </section> : null}

    {preview ? <div className="production-plan-preview">
      <header className="production-plan-result-heading">
        <div><strong>{formatDate(preview.weekStart)} haftası · {selectedSavedPlan?.status === "SUPERSEDED" ? "Arşiv planı" : "Plan kaydedildi"}</strong><small>{preview.summary.importedRowCount} mamul satırı · {preview.summary.internalProductionRowCount ?? 0} ara üretim satırı</small></div>
        <CheckCircle2 size={19} aria-hidden="true" />
      </header>
      {(preview.summary.missingWorkOrderCount ?? 0) + (preview.summary.ambiguousWorkOrderCount ?? 0) > 0 ? <p className="weekly-plan-upload-help" role="status">{(preview.summary.missingWorkOrderCount ?? 0) + (preview.summary.ambiguousWorkOrderCount ?? 0)} satırın Mikro iş emri eşleşmesi bekleniyor. Bu satırlar operatörde başlatılabilir iş olarak gösterilmez.</p> : null}
      <details className="weekly-plan-details" key={selectedPlanId}><summary>Malzeme ihtiyaçları ve iş emirleri</summary>

      <div className="production-plan-tabs" aria-label="Plan sonucu görünümü">
        {preview.materialAssessment !== "NOT_REQUESTED" ? <>
        <button type="button" className={activeTab === "SHORTAGES" ? "is-active" : ""} aria-pressed={activeTab === "SHORTAGES"} onClick={() => selectTab("SHORTAGES")}>Satın almaya gönder <strong>{preview.materials.filter((material) => procurementView(material) === "PURCHASE").length}</strong></button>
        <button type="button" className={activeTab === "REVIEW" ? "is-active" : ""} aria-pressed={activeTab === "REVIEW"} onClick={() => selectTab("REVIEW")}>İnceleme gereken <strong>{preview.materials.filter((material) => procurementView(material) === "REVIEW").length}</strong></button>
        <button type="button" className={activeTab === "ORDERS" ? "is-active" : ""} aria-pressed={activeTab === "ORDERS"} onClick={() => selectTab("ORDERS")}>Sipariş / transfer takibi <strong>{preview.materials.filter((material) => procurementView(material) === "ORDERS").length}</strong></button>
        <button type="button" className={activeTab === "MATERIALS" ? "is-active" : ""} aria-pressed={activeTab === "MATERIALS"} onClick={() => selectTab("MATERIALS")}>Tüm hesaplananlar <strong>{preview.summary.materialCount}</strong></button>
        </> : null}
        <button type="button" className={activeTab === "PLAN" ? "is-active" : ""} aria-pressed={activeTab === "PLAN"} onClick={() => selectTab("PLAN")}>Plan doğrulama <strong>{preview.rows.length}</strong></button>
      </div>

      {activeTab === "REVIEW" && (preview.internalStockAlerts?.length ?? 0) > 0 ? <section className="production-plan-internal-stock-alerts" aria-labelledby="production-plan-internal-stock-title">
        <div>
          <AlertTriangle size={18} />
          <span><strong id="production-plan-internal-stock-title">Geri dönüşüm stoğu kontrol edilmeli</strong><small>Bu kalemler satın alma ihtiyacına dahil edilmedi.</small></span>
        </div>
        <ul>{preview.internalStockAlerts?.map((alert) => <li key={alert.materialCode}>
          <span><strong>{alert.materialName || alert.materialCode}</strong><small>{alert.materialCode} · İlk ihtiyaç {formatDate(alert.firstRequiredAt)}</small></span>
          <span><small>İhtiyaç</small><strong>{formatQuantity(alert.requiredQuantity, alert.unit)}</strong></span>
          <span><small>İç stok eksiği</small><strong>{formatQuantity(alert.shortageQuantity, alert.unit)}</strong></span>
        </li>)}</ul>
      </section> : null}

      {!currentAssessment ? <p className="production-material-assessment-note" role="status">{preview.materialAssessment === "NOT_REQUESTED" ? "İş emirleri plana aktarıldı. Reçete ve stok hesabı henüz yapılmadı; gerekiyorsa Reçete ve stokla karşılaştır düğmesini kullanın." : "Bu kayıt güncel satın alma hesabıyla doğrulanmadı veya arşiv sürümüdür. Güncel planı seçip Mikro’dan yenileyin; doğrulanana kadar yeni talep oluşturulamaz."}</p> : null}
      {activeRequestError ? <p className="production-material-assessment-note" role="alert">{activeRequestError}</p> : !activeRequestsReady ? <p role="status">Mevcut satın alma talepleri kontrol ediliyor…</p> : null}
      {activeTab === "REVIEW" && preview.procurementAssessment ? <details className="production-plan-warnings">
        <summary><AlertTriangle size={15} /> {groupedReviews.length} toplu kontrol · Son hesap {validRefreshTime ? new Intl.DateTimeFormat("tr-TR", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Istanbul" }).format(new Date(preview.refreshedAt)) : "Yenileme gerekli"}</summary>
        {groupedReviews.length ? <ul>{groupedReviews.map((issue) => <li key={issue.message}><details><summary>{issue.message} · {issue.materialCodes.length} malzeme · {issue.sourceProducts.length} ürün</summary>{issue.materialCodes.length ? <p>Malzeme: {issue.materialCodes.join(", ")}</p> : null}{issue.sourceProducts.length ? <p>Ürün: {issue.sourceProducts.join(", ")}</p> : null}</details></li>)}</ul> : <p>Hesapta bildirilen veri sorunu yok.</p>}
      </details> : null}
      {activeTab === "PLAN" && preview.warnings.length > 0 ? <details className="production-plan-warnings">
        <summary><AlertTriangle size={15} /> {preview.warnings.length} kontrol notu</summary>
        <ul>{preview.warnings.map((warning) => <li key={warning}>{warning}</li>)}</ul>
      </details> : null}

      {requestReviewOpen ? createPortal(<div className="production-plan-request-drawer-layer">
        <button className="production-plan-request-drawer-backdrop" type="button" aria-label="Talep önizlemesini kapat" onClick={() => setRequestReviewOpen(false)} />
        <aside className="production-plan-request-review" role="dialog" aria-modal="true" aria-labelledby="production-plan-request-review-title">
        <header>
          <div className="production-plan-request-heading">
            <span>Satın alma talebi</span>
            <h3 id="production-plan-request-review-title">{requestReviewStep === "EDIT" ? "Talep hazırlığı" : "Son kontrol"}</h3>
            <small>{requestReviewStep === "EDIT" ? "Miktar ve ihtiyaç tarihlerini düzenleyin." : "Bu adımdan sonra talepler onay akışına gönderilir."}</small>
          </div>
          <div className="production-plan-request-header-actions">
            <strong>{requestDrafts.length} kalem</strong>
            <button type="button" aria-label="Talep önizlemesini kapat" onClick={() => setRequestReviewOpen(false)}><X size={17} /></button>
          </div>
        </header>
        {requestReviewStep === "EDIT" ? <>{overdueDraftMaterials.length > 0 ? <section className="production-plan-overdue-confirmation" aria-labelledby="production-plan-overdue-title">
          <div>
            <AlertTriangle size={19} />
            <span><strong id="production-plan-overdue-title">Gecikmiş açık sipariş bulundu</strong><small>Bu siparişler plan stokuna eklenmedi. Yeni talep açmadan önce durumlarını kontrol edin.</small></span>
          </div>
          <ul>{overdueDraftMaterials.slice(0, 3).map((material) => <li key={material.materialCode}>
            <strong>{material.materialName || material.materialCode}</strong>
            <span>{formatQuantity(material.overdueInboundQuantity ?? 0, material.unit)} gecikmiş · {material.overdueOrderCount ?? 0} sipariş / {material.overdueOrderLineCount ?? 0} satır</span>
            <small>{material.overdueFirstDeliveryAt ? formatDate(material.overdueFirstDeliveryAt) : "Tarih yok"}{material.overdueLastDeliveryAt && material.overdueLastDeliveryAt !== material.overdueFirstDeliveryAt ? ` – ${formatDate(material.overdueLastDeliveryAt)}` : ""}</small>
          </li>)}{overdueDraftMaterials.length > 3 ? <li className="is-more"><strong>+{overdueDraftMaterials.length - 3} kalem daha</strong><span>Seçimde gecikmiş siparişi bulunan diğer malzemeler</span></li> : null}</ul>
          <label className="production-plan-overdue-acknowledgement"><input type="checkbox" checked={overdueOverrideAcknowledged} onChange={(event) => setOverdueOverrideAcknowledged(event.target.checked)} /><span>Gecikmiş siparişleri kontrol ettim; buna rağmen yeni talep gerekiyor.</span></label>
          <label className="production-plan-overdue-reason"><span>Gerekçe</span><textarea value={overdueOverrideReason} onChange={(event) => setOverdueOverrideReason(event.target.value)} placeholder="Örn. Tedarikçi teslim edemeyeceğini bildirdi; mevcut sipariş iptal edilecek." rows={2} /></label>
        </section> : null}<div className="production-plan-request-columns" aria-hidden="true">
          <span>Malzeme</span>
          <span>Talep miktarı</span>
          <span>Birim</span>
          <span>İhtiyaç tarihi</span>
        </div>
        <div className="production-plan-request-drafts">
          {requestDrafts.map((draft) => {
            const material = preview.materials.find((item) => item.materialCode === draft.materialCode);
            if (!material) return null;
            return <div className="production-plan-request-draft" key={draft.materialCode}>
              <div
                className="production-plan-request-material"
                title={material.materialName || material.materialCode}
              ><strong>{material.materialName || material.materialCode}</strong><small><b>{material.materialCode}</b><i>·</i><span>Hesaplanan açık</span><em>{formatQuantity(material.productionNeedQuantity, material.unit)}</em></small></div>
              <label><span>Talep miktarı</span><input aria-label={`${material.materialName || material.materialCode} talep miktarı`} type="number" min="0.001" max={requestQuantity(material.purchaseQuantity ?? 0, material.unit)} step="any" value={draft.quantity} onChange={(event) => setRequestDrafts((current) => current.map((item) => item.materialCode === draft.materialCode ? { ...item, quantity: Number(event.target.value) } : item))} /></label>
              <span className="production-plan-request-unit">{material.unit}</span>
              <label><span>İhtiyaç tarihi</span><input aria-label={`${material.materialName || material.materialCode} ihtiyaç tarihi`} type="date" min={today} value={draft.due} onChange={(event) => setRequestDrafts((current) => current.map((item) => item.materialCode === draft.materialCode ? { ...item, due: event.target.value } : item))} /></label>
            </div>;
          })}
        </div>
        </> : <div className="production-plan-batch-review">
          <section className="production-plan-batch-review-intro">
            <CheckCircle2 size={22} />
            <div>
              <strong>{requestDrafts.length} ayrı talep gönderilecek</strong>
              <p>Talepler tek bir plan grubu altında izlenecek; işlem sırasında bir satır başarısız olursa hiçbir talep gönderilmeyecek.</p>
            </div>
          </section>
          <div className="production-plan-batch-metrics" aria-label="Gönderim özeti">
            <article><small>Hammadde</small><strong>{rawMaterialRequestCount}</strong></article>
            <article><small>Ambalaj</small><strong>{packagingRequestCount}</strong></article>
            <article><small>Değiştirilen miktar</small><strong>{changedRequestCount}</strong></article>
            <article className={criticalRequestCount > 0 ? "is-warning" : ""}><small>Bugün / gecikmiş</small><strong>{criticalRequestCount}</strong></article>
          </div>
          <div className="production-plan-batch-checks">
            <div><Check size={16} /><span>Miktar ve tarihler doğrulandı</span></div>
            <div><Check size={16} /><span>Her malzeme için ayrı satın alma talebi açılacak</span></div>
            <div><Check size={16} /><span>Tekrar tıklama aynı talepleri ikinci kez oluşturmayacak</span></div>
          </div>
          <p className="production-plan-batch-note"><strong>Gönder</strong> dediğinizde Satın Alma ekibinin onay süreci başlar. Henüz hazır değilseniz taslak olarak kaydedebilirsiniz.</p>
        </div>}
        <footer>
          {requestReviewStep === "EDIT" ? <>
            <button className="secondary-button" type="button" disabled={creatingRequests} onClick={() => setRequestReviewOpen(false)}>Vazgeç</button>
            <button className="secondary-button" type="button" disabled={creatingRequests || requestDraftInvalid} onClick={() => void saveRequestBatch(false)}>
              {creatingRequests ? <LoaderCircle className="is-spinning" size={16} /> : null}
              Taslak kaydet
            </button>
            <button className="primary-button" type="button" disabled={creatingRequests || requestDraftInvalid} onClick={reviewSelectedRequests}>
              Kontrol et ve gönder <ChevronRight size={16} />
            </button>
          </> : <>
            <button className="secondary-button" type="button" disabled={creatingRequests} onClick={() => setRequestReviewStep("EDIT")}><ChevronLeft size={16} /> Geri</button>
            <button className="primary-button" type="button" disabled={creatingRequests} onClick={() => void saveRequestBatch(true)}>
              {creatingRequests ? <LoaderCircle className="is-spinning" size={16} /> : <FilePlus2 size={16} />}
              {creatingRequests ? "Onaya gönderiliyor…" : `${requestDrafts.length} talebi onaya gönder`}
            </button>
          </>}
        </footer>
        </aside>
      </div>, document.body) : selectedMaterialCodes.size > 0 ? <div className="production-plan-selection-bar" role="status">
        <strong>{selectedMaterialCodes.size} malzeme seçildi</strong>
        <div>
          <button type="button" onClick={() => setSelectedMaterialCodes(new Set())}>Seçimi temizle</button>
          <button className="primary-button" type="button" onClick={openRequestReview}><FilePlus2 size={16} /> Talep önizlemesi</button>
        </div>
      </div> : null}
      {activeTab === "SHORTAGES" ? <p className="production-material-assessment-note">Yalnız doğrulanmış yeni alım miktarları listelenir. Malzemeleri seçip talep önizlemesinden satın almaya gönderin. İnceleme bekleyen kalemler bu listeye dahil değildir.</p> : null}
      {activeTab !== "PLAN" && visibleMaterials.length === 0 ? <p className="production-material-assessment-note" role="status">{activeTab === "SHORTAGES" ? "Şu anda gönderilmeye hazır yeni alım yok. Bu, tüm ihtiyaçların karşılandığı anlamına gelmez; inceleme gereken kalemleri kontrol edin." : "Bu görünümde malzeme yok."}</p> : null}
      {activeTab !== "PLAN" ? <div className="production-plan-table-wrap">
        <table className="production-plan-table">
          <colgroup><col className="is-select-column" /><col className="is-date-column" /><col className="is-material-column" /><col /><col /><col /><col /></colgroup>
          <thead><tr><th className="is-select"><input type="checkbox" aria-label="Sayfadaki satın alma ihtiyaçlarını seç" checked={allVisibleSelected} disabled={visibleSelectableMaterials.length === 0} onChange={(event) => setSelectedMaterialCodes((current) => {
            const next = new Set(current);
            for (const material of visibleSelectableMaterials) {
              if (event.target.checked) next.add(material.materialCode);
              else next.delete(material.materialCode);
            }
            return next;
          })} /></th><th>İhtiyaç tarihi</th><th>Malzeme</th><th className="is-number">Kalan plan ihtiyacı</th><th className="is-number">Stok / sipariş</th><th className="is-number">Plan sonu bakiye</th><th className="is-number">{activeTab === "SHORTAGES" ? "Gönderilecek miktar" : "Sonuç"}</th></tr></thead>
          <tbody>{visibleMaterials.map((material) => {
            const hasOverdueOrder = (material.overdueInboundQuantity ?? 0) > 0;
            const orders = material.inboundEvents ?? [];
            const lateQuantity = orders.filter((order) => order.lateForPlan && !order.overdue).reduce((sum, order) => sum + order.quantity, 0);
            const selectable = activeRequestsReady && canRequestMaterial(material, currentAssessment);
            return <tr className={material.productionNeedQuantity > 0 ? "has-shortage" : "is-stock-covered"} key={material.materialCode}>
            <td className="is-select">{activeRequests[material.materialCode] ? <span title={`Talep mevcut: ${activeRequests[material.materialCode].requestNo}`}><CheckCircle2 size={17} /></span> : <input type="checkbox" aria-label={`${material.materialName || material.materialCode} malzemesini seç`} checked={selectedMaterialCodes.has(material.materialCode)} disabled={!selectable} onChange={() => toggleMaterial(material.materialCode)} />}</td>
            <td className="production-plan-date-cell">
              <span>{material.firstRequiredAt !== material.lastRequiredAt ? "İlk ihtiyaç" : "İhtiyaç tarihi"}</span>
              <strong>{formatDate(material.firstRequiredAt)}</strong>
              {material.firstRequiredAt !== material.lastRequiredAt ? <small>Son ihtiyaç · {formatDate(material.lastRequiredAt)}</small> : null}
            </td>
            <td><button className="production-plan-material-link" type="button" disabled={!selectable || Boolean(activeRequests[material.materialCode])} onClick={() => openSingleRequestReview(material.materialCode)}>{material.materialName || "Adsız malzeme"}</button><small>{material.materialCode} · {material.sourceProductCount} ürün{activeRequests[material.materialCode] ? ` · ${activeRequests[material.materialCode].requestNo} mevcut` : ""}</small><ProductionMaterialEvidence material={material} unit={material.unit} /></td>
            <td className="is-number"><strong>{formatQuantity(material.totalRequiredQuantity, material.unit)}</strong><small>Plan tüketimi</small></td>
            <td className="is-number"><strong>{formatQuantity(material.onHandQuantity, material.unit)}</strong><small>Hesaba alınan stok</small><small>{material.inboundQuantity > 0 ? `Hesaba alınan giriş: ${formatQuantity(material.inboundQuantity, material.unit)}` : orders.length ? "Hesaba alınan giriş yok" : hasOverdueOrder ? "Gecikmiş sipariş var" : currentAssessment ? "Açık sipariş kaydı yok" : "Siparişler yenilenmeli"}</small>{hasOverdueOrder ? <small>Gecikmiş: {formatQuantity(material.overdueInboundQuantity ?? 0, material.unit)}</small> : null}{lateQuantity > 0 ? <small>Geç teslim: {formatQuantity(lateQuantity, material.unit)}</small> : null}</td>
            <td className={`is-number ${material.projectedStockQuantity < 0 ? "is-short" : ""}`}><strong>{formatQuantity(material.projectedStockQuantity, material.unit)}</strong><small>{hasOverdueOrder ? "Gecikmiş sipariş dahil değil" : "Plan sonrası"}</small></td>
            <td className="is-number"><strong>{procurementView(material) === "PURCHASE" ? formatQuantity(material.purchaseQuantity ?? 0, material.unit) : procurementActionLabel(material)}</strong><small>{procurementView(material) === "PURCHASE" ? "Yeni satın alma" : material.productionNeedQuantity > 0 ? `Hesaplanan açık: ${formatQuantity(material.productionNeedQuantity, material.unit)}` : "Yeni alım önerilmedi"}</small></td>
          </tr>;
          })}</tbody>
        </table>
      </div> : <div className="production-plan-table-wrap">
        <table className="production-plan-table is-plan">
          <thead><tr><th>Tarih</th><th>Üretilecek mamul</th><th>Mikro bağlantısı</th><th className="is-number">Haftalık hedef</th><th className="is-number">Kalan hesap</th><th>Durum</th></tr></thead>
          <tbody>{visiblePlanRows.map((row) => <tr key={row.id}>
            <td>{formatDate(row.requiredAt)}</td>
            <td><strong>{row.outputName || "Adsız mamul"}</strong><small>{row.outputCode} · {productStageLabel(row)}{row.recipeMaterialCount != null ? ` · ${row.recipeMaterialCount} reçete kontrol kalemi` : ""}</small></td>
            <td className="production-plan-work-order-cell">{workOrderConnection(row)}<ProductionWorkOrderTree tree={row.workOrderTree} /></td>
            <td className="is-number">{formatQuantity(row.plannedQuantity, row.productUnit ?? "")}</td>
            <td className="is-number">{formatQuantity(row.effectiveQuantity, row.productUnit ?? "")}</td>
            <td><span className={`production-plan-status is-${row.status.toLocaleLowerCase("tr-TR").replaceAll("_", "-")}`}>{planRowStatusLabel(row)}</span></td>
          </tr>)}</tbody>
        </table>
      </div>}
      {shouldPaginate ? <nav className="production-plan-pagination" aria-label="Liste sayfaları">
        <span>{activeItemCount > 0 ? `${pageStart + 1}–${pageEnd} / ${activeItemCount}` : "Kayıt yok"}</span>
        <div>
          <button type="button" aria-label="Önceki sayfa" disabled={page === 1} onClick={() => setPage((current) => Math.max(1, current - 1))}><ChevronLeft size={17} /></button>
          <strong>{page} / {totalPages}</strong>
          <button type="button" aria-label="Sonraki sayfa" disabled={page === totalPages} onClick={() => setPage((current) => Math.min(totalPages, current + 1))}><ChevronRight size={17} /></button>
        </div>
      </nav> : null}
      </details>
    </div> : null}
  </section>;
}
